import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForgetPassword, resetPassword } from '../services/authentication';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isPending, setIsPending] = useState(false);

  const forgetPasswordMutation = useForgetPassword(
    () => {
      setMessage('Password reset email sent successfully. Please check your inbox.');
      setError('');
    },
    (err: any) => {
      setError(err.message || 'An unexpected error occurred.');
      setMessage('');
    }
  );

  const handleForgetSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setMessage('');
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    forgetPasswordMutation.mutate({ email });
  };

  const handleResetSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsPending(true);
    const { error: resetError } = await resetPassword({
      newPassword,
      token: token || ''
    });
    setIsPending(false);

    if (resetError) {
      setError(resetError.message || 'Failed to reset password.');
    } else {
      setMessage('Password reset successfully. You can now log in.');
    }
  };

  return (
    <div className="flex justify-center items-center h-dvh bg-surface-container-low">
      <div className="card w-full max-w-sm ">
        <div className="card-body">
          <h2 className="text-2xl font-display font-bold text-on-surface text-center">
            {token ? 'Set New Password' : 'Reset Password'}
          </h2>

          {error && (
            <div className="alert alert-error py-2 text-sm mt-4">
              {error}
            </div>
          )}
          {message && (
            <div className="alert alert-success py-2 text-sm mt-4">
              {message}
            </div>
          )}

          {token ? (
            <form onSubmit={handleResetSubmit} className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="label">
                  <span className="label-text">New Password</span>
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  placeholder="••••••••"
                  className="input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isPending}
                  required
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="label">
                  <span className="label-text">Confirm New Password</span>
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="••••••••"
                  className="input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isPending}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-full mt-6"
                disabled={isPending}
              >
                {isPending ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgetSubmit} className="space-y-4">
              <div>
                <label htmlFor="reset-email" className="label">
                  <span className="label-text">Email</span>
                </label>
                <input
                  type="email"
                  id="reset-email"
                  placeholder="Enter your email"
                  name="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={forgetPasswordMutation.isPending}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-full mt-6"
                disabled={forgetPasswordMutation.isPending}
              >
                {forgetPasswordMutation.isPending ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          <div className="text-center mt-4">
            <Link to="/login" className="text-sm text-on-surface-variant/40 hover:text-tertiary transition-colors">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;